import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Plus, Loader2, Maximize2, Minimize2, BookOpen, Globe, Search, X, Bot, ChevronDown, ShieldCheck, GitCompare, Lightbulb, Sparkles, ListTodo, Bell, CheckCircle2, Clock, Trash2, AlertTriangle, Monitor, StickyNote, ExternalLink, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { chatAPI, agentAPI, brainyAPI, notesAPI, externalAgentsAPI, sourceAPI } from '../api';
import { SourceDetailPopup } from './SourcesPanel';
import WorkspaceIntelligence from './WorkspaceIntelligence';

// Module-scope constants — extracted from JSX to prevent new-object-on-every-render perf hit
const DOMPURIFY_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'srcdoc'],
};
const REMARK_PLUGINS = [remarkGfm];

export default function ChatPanel({ workspaceId, totalSources, chatFullscreen, setChatFullscreen }) {
  const [chatSessions, setChatSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [pendingMessages, setPendingMessages] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [chatMode, setChatMode] = useState('knowledge');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [brainyEnabled, setBrainyEnabled] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const chatEndRef = useRef(null);
  // Ref mirror of activeSession so async auto-resume can read the *current* value
  // rather than a stale closure from the initial render. Without this, a user clicking
  // a question pill BEFORE listSessions resolves would have their fresh session
  // (created inside sendMessage) silently overwritten by the auto-resume path.
  const activeSessionRef = useRef(null);
  useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);

  useEffect(() => {
    let cancelled = false;
    chatAPI.listSessions(workspaceId).then(s => {
      if (cancelled) return;
      const list = s.data || [];
      setChatSessions(list);
      // Auto-resume last session: load most-recent session messages so users continue
      // their conversation when re-opening the workspace. Read activeSession from a ref
      // to avoid the stale-closure race where a user clicks a suggested-question pill
      // before listSessions resolves — that click already created a fresh session and
      // we must not silently switch them to a different (older, possibly empty) one.
      if (list.length > 0 && !activeSessionRef.current) {
        const latest = list[0];
        setActiveSession(latest.id);
        setSessionLoading(true);
        chatAPI.getMessages(latest.id)
          .then(r => {
            if (cancelled) return;
            const fetched = r.data || [];
            // Race-condition guard: if the user already started typing/clicking and
            // sent a message before this resolved, never wipe their messages with the
            // fetched (possibly empty) history.
            setMessages(prev => (prev.length > 0 ? prev : fetched));
          })
          .catch(() => {})
          .finally(() => { if (!cancelled) setSessionLoading(false); });
      }
    }).catch(() => {});
    agentAPI.listAvailable(workspaceId).then(r => setAvailableAgents(r.data)).catch(() => {});
    brainyAPI.getConfig().then(r => {
      const enabled = r.data.enabled !== false;
      setBrainyEnabled(enabled);
      if (!enabled) setChatMode(prev => prev === 'brainy' ? 'knowledge' : prev);
    }).catch(() => setBrainyEnabled(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingMessages]);

  // Listen for "Ask about this" follow-ups dispatched from MindMapLeafDrawer.
  // The drawer stashes a contextual prompt and fires `bkw:askFollowup`; we send it
  // immediately so the user lands directly in a conversation about the clicked leaf.
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.detail || ev.detail.workspaceId !== workspaceId) return;
      const prompt = ev.detail.prompt;
      if (typeof prompt === 'string' && prompt.trim()) {
        try { sessionStorage.removeItem(`bkw:wsAskFollowup:${workspaceId}`); } catch { /* ignore */ }
        // Defer one tick so any in-flight modal close finishes first.
        setTimeout(() => sendMessage(prompt), 50);
      }
    };
    window.addEventListener('bkw:askFollowup', handler);
    return () => window.removeEventListener('bkw:askFollowup', handler);
    // sendMessage is intentionally not a dep — it's defined in the same closure and
    // creating a stable callback would bloat this component. The `workspaceId` guard
    // on the event prevents stale-closure cross-talk between workspace switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const createNewSession = async () => {
    try {
      const r = await chatAPI.createSession(workspaceId);
      setActiveSession(r.data.id);
      setMessages([]);
      setChatSessions(p => [{ ...r.data, date_created: new Date().toISOString() }, ...p]);
    } catch (e) { console.error(e); }
  };

  const sendMessage = async (overrideText) => {
    const rawText = typeof overrideText === 'string' ? overrideText : chatInput;
    if (!rawText.trim()) return;
    const msgText = rawText;
    if (typeof overrideText !== 'string') setChatInput('');
    let sessionId = activeSession;
    if (!sessionId) {
      try {
        const res = await chatAPI.createSession(workspaceId);
        sessionId = res.data.id;
        setActiveSession(sessionId);
        setChatSessions(prev => [{ ...res.data, date_created: new Date().toISOString() }, ...prev]);
      } catch (e) { console.error(e); return; }
    }
    const tempId = `pending-${Date.now()}`;
    const userMsg = { id: `user-${tempId}`, role: 'user', content: msgText, citations: [] };
    setMessages(prev => [...prev, userMsg]);
    setPendingMessages(prev => [...prev, tempId]);

    // 3rd-party external agent: bypass SSE pipeline, call proxy endpoint directly
    if (chatMode === 'agent' && selectedAgent && selectedAgent.type === 'external') {
      try {
        setStatusMessage(`Calling ${selectedAgent.name}...`);
        const r = await externalAgentsAPI.run(selectedAgent.id, msgText);
        const data = r.data || {};
        setMessages(prev => [...prev, {
          id: `ext-${tempId}`,
          role: 'assistant',
          content: data.response || '(empty response)',
          is_html: !!data.is_html,
          metadata: data.metadata || {},
          agent_name: data.agent_name,
          citations: [],
        }]);
      } catch (e) {
        console.error(e);
        setMessages(prev => [...prev, { id: `err-${tempId}`, role: 'assistant', content: `Error: ${e.response?.data?.detail || e.message}`, citations: [] }]);
      } finally {
        setPendingMessages(prev => prev.filter(id => id !== tempId));
        setStatusMessage('');
      }
      return;
    }

    // SSE Streaming
    try {
      const streamingMsgId = `streaming-${tempId}`;
      setMessages(prev => [...prev, { id: streamingMsgId, role: 'assistant', content: '', citations: [], streaming: true }]);

      const response = await chatAPI.sendMessageStream(sessionId, {
        content: msgText,
        document_ids: null,
        mode: chatMode,
        agent_id: chatMode === 'agent' && selectedAgent ? selectedAgent.id : null,
        agent_type: chatMode === 'agent' && selectedAgent ? selectedAgent.type : null,
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      let finalCitations = [];
      let finalMsgId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'chunk') {
              fullText += data.content;
              setStatusMessage('');
              setMessages(prev => prev.map(m => m.id === streamingMsgId ? { ...m, content: fullText } : m));
            } else if (data.type === 'status') {
              setStatusMessage(data.message || '');
            } else if (data.type === 'done') {
              finalCitations = data.citations || [];
              finalMsgId = data.id;
              setStatusMessage('');
            } else if (data.type === 'user_message') {
              setMessages(prev => prev.map(m => m.id === `user-${tempId}` ? { ...m, id: data.id } : m));
            }
          } catch (parseErr) { /* skip malformed SSE */ }
        }
      }
      // Finalize streaming message
      setMessages(prev => prev.map(m => m.id === streamingMsgId ? { ...m, id: finalMsgId || streamingMsgId, content: fullText, citations: finalCitations, streaming: false } : m));
      const sessRes = await chatAPI.listSessions(workspaceId);
      setChatSessions(sessRes.data);
    } catch (e) {
      console.error(e);
      setMessages(prev => [...prev, { id: `err-${tempId}`, role: 'assistant', content: 'Error generating response. Please try again.', citations: [] }]);
    } finally {
      setPendingMessages(prev => prev.filter(id => id !== tempId));
    }
  };

  const loadSession = async (sid) => {
    setActiveSession(sid);
    setSessionLoading(true);
    setMessages([]);
    setHistoryOpen(false);
    try { const res = await chatAPI.getMessages(sid); setMessages(res.data); } catch (e) { console.error(e); }
    finally { setSessionLoading(false); }
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-white" data-testid="chat-panel">
      <ChatSessionBar
        chatSessions={chatSessions} activeSession={activeSession}
        historyOpen={historyOpen} setHistoryOpen={setHistoryOpen}
        historySearch={historySearch} setHistorySearch={setHistorySearch}
        chatFullscreen={chatFullscreen} setChatFullscreen={setChatFullscreen}
        onNewSession={createNewSession} onLoadSession={loadSession}
      />
      <MessagesArea messages={messages} pendingMessages={pendingMessages} sessionLoading={sessionLoading} chatEndRef={chatEndRef} workspaceId={workspaceId} statusMessage={statusMessage} totalSources={totalSources} onAskQuestion={(q) => sendMessage(q)} />
      <ChatInput chatInput={chatInput} setChatInput={setChatInput} totalSources={totalSources} onSend={sendMessage} chatMode={chatMode} setChatMode={setChatMode} availableAgents={availableAgents} selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent} brainyEnabled={brainyEnabled} />
    </div>
  );
}

function ChatSessionBar({ chatSessions, activeSession, historyOpen, setHistoryOpen, historySearch, setHistorySearch, chatFullscreen, setChatFullscreen, onNewSession, onLoadSession }) {
  const activeTitle = chatSessions.find(s => s.id === activeSession)?.title || 'Untitled Chat';
  return (
    <div className="h-9 border-b border-[#E5E7EB] flex items-center px-3 gap-2 flex-shrink-0">
      <button onClick={onNewSession} className="flex-shrink-0 h-6 px-2 text-[11px] font-medium text-[#6590FF] border border-[#6590FF]/30 rounded-full hover:bg-[#6590FF]/5 flex items-center gap-1 transition-colors" data-testid="new-chat-btn">
        <Plus className="w-3 h-3" /> New
      </button>
      {activeSession && <span className="text-sm text-[#111827] font-medium truncate max-w-[200px]">{activeTitle}</span>}
      <div className="flex-1" />
      <div className="relative">
        <button onClick={() => setHistoryOpen(!historyOpen)} className="flex-shrink-0 h-7 px-3 text-sm font-medium text-[#4B5563] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-full flex items-center gap-1.5 transition-colors" data-testid="chat-history-btn">
          <MessageSquare className="w-3.5 h-3.5" /> History ({chatSessions.length})
        </button>
        {historyOpen && <HistoryPopup sessions={chatSessions} activeSession={activeSession} search={historySearch} setSearch={setHistorySearch} onLoad={onLoadSession} />}
      </div>
      <button onClick={() => setChatFullscreen(f => !f)} className="flex-shrink-0 p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" data-testid="chat-fullscreen-btn">
        {chatFullscreen ? <Minimize2 className="w-4 h-4 text-[#6590FF]" /> : <Maximize2 className="w-4 h-4 text-[#4B5563]" />}
      </button>
    </div>
  );
}

function HistoryPopup({ sessions, activeSession, search, setSearch, onLoad }) {
  const filtered = sessions.filter(s => !search || s.title?.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="absolute right-0 top-8 w-72 bg-white border border-[#E5E7EB] rounded-xl shadow-xl z-50" data-testid="chat-history-popup">
      <div className="p-2 border-b border-[#E5E7EB]">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search chats..."
          className="w-full h-8 px-2.5 text-base border border-[#E5E7EB] rounded-xl bg-[#F9FAFB] focus:outline-none focus:border-[#6590FF]" data-testid="chat-history-search" />
      </div>
      <div className="max-h-64 overflow-y-auto">
        {filtered.map(s => (
          <button key={s.id} onClick={() => onLoad(s.id)}
            className={`w-full text-left px-3 py-2 hover:bg-[#F3F4F6] transition-colors border-b border-[#F3F4F6] last:border-b-0 ${activeSession === s.id ? 'bg-[#6590FF]/5' : ''}`}
            data-testid={`history-session-${s.id}`}>
            <p className={`text-base font-medium truncate ${activeSession === s.id ? 'text-[#6590FF]' : 'text-[#111827]'}`}>{s.title || 'Untitled Chat'}</p>
            <p className="text-xs text-[#9CA3AF] mt-0.5">{s.date_created ? new Date(s.date_created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
          </button>
        ))}
        {sessions.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-4">No chat history</p>}
      </div>
    </div>
  );
}

function MessagesArea({ messages, pendingMessages, sessionLoading, chatEndRef, workspaceId, statusMessage, totalSources, onAskQuestion }) {
  const hasMessages = messages.length > 0 || pendingMessages.length > 0;
  return (
    <div className="flex-1 overflow-y-auto" data-testid="chat-messages-area">
      <div className="max-w-3xl mx-auto px-4 py-4">
        {sessionLoading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-10 h-10 text-[#6590FF] animate-spin mb-3" />
            <p className="text-base text-[#6B7280]">Loading chat...</p>
          </div>
        ) : (
          <>
            {/* Workspace Intelligence — pinned at top whenever sources exist. */}
            {/* Collapsed by default once a conversation has started so it doesn't dominate the view, */}
            {/* but the user can expand it back at any time. */}
            {totalSources > 0 && workspaceId && (
              <div className="mb-4">
                <WorkspaceIntelligence
                  workspaceId={workspaceId}
                  onAskQuestion={onAskQuestion}
                  defaultCollapsed={hasMessages}
                />
              </div>
            )}
            {hasMessages ? (
              <div className="space-y-3">
                {messages.map((msg, idx) => <ChatMessage key={msg.id || idx} msg={msg} workspaceId={workspaceId} />)}
                {pendingMessages.length > 0 && <TypingIndicator statusMessage={statusMessage} />}
                <div ref={chatEndRef} />
              </div>
            ) : (
              <EmptyChatLabel totalSources={totalSources} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EmptyChatLabel({ totalSources }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <MessageSquare className="w-9 h-9 text-[#D1D5DB] mb-3" />
      <h3 className="text-base font-semibold text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
        {totalSources > 0 ? 'Ready when you are' : 'Start a conversation'}
      </h3>
      <p className="text-sm text-[#6B7280] max-w-md">
        {totalSources > 0
          ? 'Ask anything about your sources, pick a suggested question above, or switch chat modes for web search and AI agents.'
          : 'Ask about your documents, search the web with External Knowledge, chat with AI Agents, or switch to Brainy for personal assistance with tasks and reminders.'}
      </p>
    </div>
  );
}

// Detects message direction so Arabic / Hebrew content automatically flips to RTL.
// Range \u0600-\u06FF = Arabic, \u0590-\u05FF = Hebrew, \u0750-\u077F = Arabic Supplement,
// \u08A0-\u08FF = Arabic Extended-A, \uFB50-\uFDFF = Arabic Presentation Forms-A,
// \uFE70-\uFEFF = Arabic Presentation Forms-B.
const RTL_REGEX = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
function detectDirection(text) {
  return RTL_REGEX.test(text || '') ? 'rtl' : 'ltr';
}

function ChatMessage({ msg, workspaceId }) {
  const isUser = msg.role === 'user';
  const [noteSaved, setNoteSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const direction = detectDirection(msg.content);

  const saveAsNote = async () => {
    if (!msg.content || saving) return;
    setSaving(true);
    try {
      await notesAPI.create(workspaceId, { content: msg.content });
      setNoteSaved(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        dir={direction}
        className={`chat-message ${isUser ? 'chat-message--user bg-[#6590FF] text-white rounded-br-sm' : 'bg-white text-[#111827] rounded-bl-sm shadow-sm'}`}
        data-testid={`chat-msg-${msg.role}`}
      >
        {isUser ? <p style={{ margin: 0 }}>{msg.content}</p> : (
          <div className="chat-message-body">
            {msg.is_html ? (
              <div className="bkw-html-response" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content, DOMPURIFY_CONFIG) }} />
            ) : (
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{msg.content}</ReactMarkdown>
            )}
            {msg.agent_name && (
              <p className="text-[11px] text-[#6B7280] mt-2 italic" style={{ marginBottom: 0 }}>via {msg.agent_name}{msg.metadata?.model && ` · ${msg.metadata.model}`}{msg.metadata?.elapsed_sec != null && ` · ${msg.metadata.elapsed_sec}s`}</p>
            )}
          </div>
        )}
        {msg.citations?.length > 0 && <Citations citations={msg.citations} workspaceId={workspaceId} />}
        {!isUser && msg.content && !msg.streaming && (
          <div className="mt-2 pt-1.5 border-t border-[#E5E7EB]/50 flex items-center" dir="ltr">
            <button onClick={saveAsNote} disabled={noteSaved || saving}
              className={`flex items-center gap-1 text-xs font-medium transition-colors ${noteSaved ? 'text-[#059669]' : 'text-[#6B7280] hover:text-[#6590FF]'}`}
              style={{ minHeight: 'unset' }}
              data-testid="save-note-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <StickyNote className="w-3 h-3" />}
              {noteSaved ? 'Saved to Notes' : 'Save Note'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Citations({ citations, workspaceId }) {
  const [openSource, setOpenSource] = useState(null);
  const [openHighlight, setOpenHighlight] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const openCitation = async (c) => {
    if (!c.document_id) return;
    setLoadingId(c.document_id);
    try {
      const res = await sourceAPI.getContent(c.document_id);
      const data = res.data || {};
      const stype = data.source_type || c.source_type || 'document';
      setOpenSource({
        id: c.document_id,
        title: data.title || c.document_title,
        source_type: stype,
        chunk_count: data.chunks?.length || 0,
        status: 'ready',
        is_active: data.is_active !== false,
        summary: data.summary || null,
      });
      setOpenHighlight({ chunkText: c.chunk_text, page: c.page_number });
    } catch (e) {
      console.error('Open citation error:', e);
    }
    setLoadingId(null);
  };

  return (
    <div className="mt-2 pt-2 border-t border-[#E5E7EB]/50">
      <p className="text-sm font-bold uppercase tracking-wider text-[#6B7280] mb-1">Sources</p>
      {citations.map((c, i) => {
        const isNote = (c.document_title || '').startsWith('Note:');
        const isLoading = loadingId === c.document_id;
        if (c.url) {
          return (
            <div key={`${c.document_title || 'cite'}-${i}`} className="text-base bg-white/80 border border-[#E5E7EB] rounded-xl p-2 mb-1">
              <a href={c.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#6590FF] hover:underline flex items-center gap-1" data-testid={`citation-link-${i}`}>
                {c.document_title}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          );
        }
        return (
          <button
            key={`${c.document_title || 'cite'}-${i}-${c.document_id || ''}`}
            onClick={() => openCitation(c)}
            disabled={!c.document_id || isLoading}
            className="w-full text-left text-base bg-white/80 border border-[#E5E7EB] hover:border-[#C7D2FE] hover:bg-[#EEF2FF]/40 rounded-xl p-2 mb-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60 flex items-start gap-2"
            data-testid={`citation-link-${i}`}>
            {isNote ? <StickyNote className="w-3.5 h-3.5 text-[#A16207] flex-shrink-0 mt-0.5" /> : <FileText className="w-3.5 h-3.5 text-[#6590FF] flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-[#6590FF]">{c.document_title || 'Untitled'}</span>
              {c.page_number > 0 && <span className="text-[#6B7280]"> · pg.{c.page_number}</span>}
              {c.chunk_text && (
                <p className="text-xs text-[#6B7280] mt-0.5 line-clamp-2 italic">"{c.chunk_text.slice(0, 140)}{c.chunk_text.length > 140 ? '…' : ''}"</p>
              )}
            </div>
            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#6590FF] flex-shrink-0 mt-0.5" /> : <ExternalLink className="w-3 h-3 text-[#9CA3AF] flex-shrink-0 mt-1" />}
          </button>
        );
      })}
      {openSource && (
        <SourceDetailPopup
          source={openSource}
          workspaceId={workspaceId}
          onClose={() => { setOpenSource(null); setOpenHighlight(null); }}
          initialTab="content"
          highlightChunkText={openHighlight?.chunkText}
          highlightPage={openHighlight?.page}
        />
      )}
    </div>
  );
}

function TypingIndicator({ statusMessage }) {
  return (
    <div className="flex justify-start">
      <div className="bg-[#F3F4F6] px-4 py-3 rounded-2xl rounded-bl-sm">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 items-center">
            <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          {statusMessage && (
            <span className="text-sm text-[#4B5563] font-medium animate-pulse" data-testid="chat-status-message">{statusMessage}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatInput({ chatInput, setChatInput, totalSources, onSend, chatMode, setChatMode, availableAgents, selectedAgent, setSelectedAgent, brainyEnabled }) {
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [brainyPanelOpen, setBrainyPanelOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setAgentDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (agentDropdownOpen && searchInputRef.current) searchInputRef.current.focus();
    if (!agentDropdownOpen) setAgentSearch('');
  }, [agentDropdownOpen]);

  const filteredAgents = availableAgents.filter(a =>
    a.name.toLowerCase().includes(agentSearch.toLowerCase()) ||
    (a.description || '').toLowerCase().includes(agentSearch.toLowerCase())
  );

  const AGENT_ICONS = { 'shield-check': ShieldCheck, 'git-compare': GitCompare, 'lightbulb': Lightbulb };

  const getPlaceholder = () => {
    if (chatMode === 'knowledge') return 'Ask about your documents...';
    if (chatMode === 'direct') return 'Ask anything using external knowledge...';
    if (chatMode === 'brainy') return 'Hey Brainy, help me with...';
    if (selectedAgent) return `Chat with ${selectedAgent.name}...`;
    return 'Select an agent to begin...';
  };

  const getModeLabel = () => {
    if (chatMode === 'knowledge') return `${totalSources} sources`;
    if (chatMode === 'direct') return 'Web search enabled';
    if (chatMode === 'brainy') return 'Personal assistant';
    if (selectedAgent) return selectedAgent.name;
    return 'No agent selected';
  };

  const getBorderStyle = () => {
    if (chatMode === 'direct') return 'bg-[#F0FDF4] border border-[#BBF7D0]';
    if (chatMode === 'agent') return `bg-[#FAF5FF] border border-[#E9D5FF]`;
    if (chatMode === 'brainy') return 'bg-[#FFF7ED] border border-[#FED7AA]';
    return 'bg-[#F3F4F6]';
  };

  const getSendColor = () => {
    if (chatMode === 'direct') return 'bg-[#059669] hover:bg-[#047857]';
    if (chatMode === 'agent') return 'bg-[#7C3AED] hover:bg-[#6D28D9]';
    if (chatMode === 'brainy') return 'bg-[#EA580C] hover:bg-[#C2410C]';
    return 'bg-[#6590FF] hover:bg-[#4B7CFF]';
  };

  return (
    <div className="border-t border-[#E5E7EB] px-4 py-3 flex-shrink-0">
      <div className="max-w-3xl mx-auto">
        {/* Mode Toggle */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center bg-[#F3F4F6] p-0.5 rounded-lg" data-testid="chat-mode-toggle">
            <button
              onClick={() => setChatMode('knowledge')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${chatMode === 'knowledge' ? 'bg-white shadow-sm text-[#6590FF]' : 'text-[#6B7280] hover:text-[#111827]'}`}
              data-testid="chat-mode-knowledge"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Workspace Knowledge</span>
              <span className="sm:hidden">WK</span>
            </button>
            <button
              onClick={() => setChatMode('direct')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${chatMode === 'direct' ? 'bg-white shadow-sm text-[#059669]' : 'text-[#6B7280] hover:text-[#111827]'}`}
              data-testid="chat-mode-direct"
            >
              <Globe className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">External Knowledge</span>
              <span className="sm:hidden">EK</span>
            </button>
            <button
              onClick={() => setChatMode('agent')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${chatMode === 'agent' ? 'bg-white shadow-sm text-[#7C3AED]' : 'text-[#6B7280] hover:text-[#111827]'}`}
              data-testid="chat-mode-agent"
            >
              <Bot className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">AI Agents</span>
              <span className="sm:hidden">AI</span>
            </button>
            {brainyEnabled === true && (
              <button
                onClick={() => setChatMode('brainy')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${chatMode === 'brainy' ? 'bg-white shadow-sm text-[#EA580C]' : 'text-[#6B7280] hover:text-[#111827]'}`}
                data-testid="chat-mode-brainy"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Brainy</span>
                <span className="sm:hidden">B</span>
              </button>
            )}
          </div>
          <span className="text-[11px] text-[#9CA3AF]">{getModeLabel()}</span>
        </div>

        {/* Brainy Quick Panel (visible in brainy mode) */}
        {chatMode === 'brainy' && (
          <BrainyQuickPanel open={brainyPanelOpen} setOpen={setBrainyPanelOpen} />
        )}

        {/* Agent Selector (visible only in agent mode) */}
        {chatMode === 'agent' && (
          <div className="mb-2 relative" ref={dropdownRef}>
            <button
              onClick={() => setAgentDropdownOpen(!agentDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-base transition-colors ${selectedAgent ? 'border-[#7C3AED]/30 bg-[#7C3AED]/5' : 'border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]'}`}
              data-testid="agent-selector-btn"
            >
              <div className="flex items-center gap-2">
                {selectedAgent ? (
                  <>
                    {(() => { const Icon = AGENT_ICONS[selectedAgent.icon] || Bot; return <Icon className="w-4 h-4" style={{ color: selectedAgent.color }} />; })()}
                    <span className="font-medium text-[#111827]">{selectedAgent.name}</span>
                    <span className="text-sm text-[#9CA3AF]">{selectedAgent.description?.slice(0, 50)}</span>
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 text-[#9CA3AF]" />
                    <span className="text-[#9CA3AF]">Select an AI agent...</span>
                  </>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${agentDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {agentDropdownOpen && (
              <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-50 max-h-72 flex flex-col" data-testid="agent-dropdown">
                {/* Search input */}
                <div className="px-3 pt-2.5 pb-1.5 border-b border-[#F3F4F6] flex-shrink-0">
                  <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-lg px-2.5 py-1.5">
                    <Search className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={agentSearch}
                      onChange={e => setAgentSearch(e.target.value)}
                      placeholder="Search agents..."
                      className="flex-1 bg-transparent border-0 text-base focus:outline-none focus:ring-0 placeholder:text-[#9CA3AF]"
                      data-testid="agent-search-input"
                    />
                    {agentSearch && (
                      <button onClick={() => setAgentSearch('')} className="p-0.5 hover:bg-[#E5E7EB] rounded">
                        <X className="w-3 h-3 text-[#9CA3AF]" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Agent list */}
                <div className="overflow-y-auto flex-1">
                  {filteredAgents.length === 0 && (
                    <div className="px-3 py-4 text-base text-[#9CA3AF] text-center">
                      {availableAgents.length === 0 ? 'No agents available' : 'No agents match your search'}
                    </div>
                  )}
                  {filteredAgents.map(agent => {
                    const Icon = AGENT_ICONS[agent.icon] || Bot;
                    const isSelected = selectedAgent?.id === agent.id;
                    return (
                      <button key={agent.id}
                        onClick={() => { setSelectedAgent(agent); setAgentDropdownOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#7C3AED]/5' : ''}`}
                        data-testid={`agent-option-${agent.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: agent.color + '15' }}>
                          <Icon className="w-4 h-4" style={{ color: agent.color }} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-base font-medium text-[#111827] flex items-center gap-1.5">
                            {agent.name}
                            {agent.type === 'builtin' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280] font-normal">Built-in</span>}
                          </div>
                          {agent.description && <div className="text-sm text-[#6B7280] truncate">{agent.description}</div>}
                        </div>
                        {isSelected && <div className="ml-auto w-2 h-2 rounded-full bg-[#7C3AED] flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input area */}
        <div className={`flex items-end gap-2 rounded-xl px-3 py-2 transition-colors ${getBorderStyle()}`}>
          <textarea value={chatInput} onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder={getPlaceholder()}
            rows={3}
            className="flex-1 border-0 bg-transparent shadow-none focus:outline-none focus:ring-0 text-lg resize-none min-h-[72px]"
            data-testid="chat-input" />
          <div className="flex flex-col items-center gap-1 pb-1">
            <button onClick={onSend} disabled={!chatInput.trim() || (chatMode === 'agent' && !selectedAgent)}
              className={`w-8 h-8 flex items-center justify-center rounded-full text-white transition-colors disabled:opacity-30 ${getSendColor()}`} data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
            {chatMode === 'brainy' && (
              <button onClick={() => setBrainyPanelOpen(p => !p)}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${brainyPanelOpen ? 'bg-[#EA580C]/10 text-[#EA580C]' : 'hover:bg-[#F3F4F6] text-[#6B7280]'}`}
                data-testid="brainy-panel-toggle" title="Tasks & Reminders">
                <ListTodo className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function BrainyQuickPanel({ open, setOpen }) {
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    if (open) loadData();
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [t, r, s] = await Promise.all([
        brainyAPI.listTasks(),
        brainyAPI.listReminders(),
        brainyAPI.getStats(),
      ]);
      setTasks(t.data);
      setReminders(r.data);
      setStats(s.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await brainyAPI.updateTask(task.id, { status: newStatus });
      loadData();
    } catch (e) { console.error(e); }
  };

  const deleteTask = async (id) => {
    try { await brainyAPI.deleteTask(id); loadData(); } catch (e) { console.error(e); }
  };

  const deleteReminder = async (id) => {
    try { await brainyAPI.deleteReminder(id); loadData(); } catch (e) { console.error(e); }
  };

  if (!open) return null;

  const PRIORITY_COLORS = { urgent: 'text-red-600 bg-red-50', high: 'text-orange-600 bg-orange-50', medium: 'text-blue-600 bg-blue-50', low: 'text-gray-500 bg-gray-50' };

  return (
    <div className="mb-2 bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-64 overflow-hidden flex flex-col" data-testid="brainy-quick-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#F3F4F6] bg-[#FFF7ED] flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#EA580C]" />
          <span className="text-sm font-bold text-[#111827]">Brainy Hub</span>
          {stats && (
            <span className="text-[11px] text-[#6B7280] bg-white px-1.5 py-0.5 rounded">
              {stats.tasks.pending} tasks &middot; {stats.reminders.active} reminders
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setActiveTab('tasks')} className={`px-2 py-1 text-[11px] font-semibold rounded-md transition-colors ${activeTab === 'tasks' ? 'bg-white text-[#EA580C] shadow-sm' : 'text-[#6B7280]'}`}
            data-testid="brainy-tab-tasks"><ListTodo className="w-3 h-3 inline mr-0.5" />Tasks</button>
          <button onClick={() => setActiveTab('reminders')} className={`px-2 py-1 text-[11px] font-semibold rounded-md transition-colors ${activeTab === 'reminders' ? 'bg-white text-[#EA580C] shadow-sm' : 'text-[#6B7280]'}`}
            data-testid="brainy-tab-reminders"><Bell className="w-3 h-3 inline mr-0.5" />Reminders</button>
          <button onClick={() => setOpen(false)} className="p-0.5 hover:bg-white/80 rounded"><X className="w-3.5 h-3.5 text-[#9CA3AF]" /></button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 p-2">
        {loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#EA580C]" /></div>
        ) : activeTab === 'tasks' ? (
          tasks.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4">No tasks yet. Ask Brainy to create one!</p>
          ) : (
            <div className="space-y-1">
              {tasks.slice(0, 10).map(task => (
                <div key={task.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F9FAFB] group" data-testid={`brainy-task-${task.id}`}>
                  <button onClick={() => toggleTaskStatus(task)} className="flex-shrink-0">
                    {task.status === 'completed' ?
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
                      <div className="w-4 h-4 rounded-full border-2 border-[#D1D5DB] group-hover:border-[#EA580C] transition-colors" />
                    }
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through text-[#9CA3AF]' : 'text-[#111827]'}`}>{task.title}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] px-1 py-0 rounded ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>{task.priority}</span>
                      {task.due_date && <span className="text-[10px] text-[#9CA3AF]"><Clock className="w-2.5 h-2.5 inline" /> {new Date(task.due_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <button onClick={() => deleteTask(task.id)} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-50 rounded transition-opacity">
                    <Trash2 className="w-3 h-3 text-[#DC2626]" />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          reminders.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4">No active reminders. Ask Brainy to set one!</p>
          ) : (
            <div className="space-y-1">
              {reminders.slice(0, 10).map(rem => (
                <div key={rem.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F9FAFB] group" data-testid={`brainy-reminder-${rem.id}`}>
                  <Bell className="w-3.5 h-3.5 text-[#EA580C] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#111827] truncate">{rem.title}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-[#6B7280]">{new Date(rem.remind_at).toLocaleString()}</span>
                      <span className="text-[10px] px-1 py-0 rounded bg-[#F3F4F6] text-[#6B7280]">{rem.channel}</span>
                    </div>
                  </div>
                  <button onClick={() => deleteReminder(rem.id)} className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-50 rounded transition-opacity">
                    <Trash2 className="w-3 h-3 text-[#DC2626]" />
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
